import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Search,
  Tag,
  FileText,
  Bot,
  UserCheck,
  ChevronRight,
  ChevronLeft,
  TrafficCone,
  HeartPulse,
  Bus,
  ShieldAlert,
  Leaf,
  GraduationCap,
  Droplets,
  Landmark,
  Lightbulb,
  HelpCircle,
  X,
  FolderPlus,
  Pencil,
  MoreHorizontal,
  Archive,
  RotateCcw,
  Trash2,
  Save,
  type LucideIcon,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useTopicosStore } from "@/stores/topicosStore";
import { useCurrentOuvidoriaStore } from "@/stores/currentOuvidoriaStore";
import { AppConfig } from "@/datasources/AppConfig";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";
import type { TopicoState } from "@/components/topicos/core/topico_repository";
import { Query } from "@/usecases/usecases";
import { TopicoRepositoryImpl } from "@/components/topicos/data/topico_repository";
import {
  Topico as TopicoFull,
  TopicoModel,
} from "@/components/topicos/core/models";

const topicoRepo = new TopicoRepositoryImpl();

interface TopicoCard {
  id: string;
  nome: string;
  categoria: string;
  descricao: string;
  contexto: string;
  metodoValidacao: "agente" | "ia";
  ativo: boolean;
  arquivado: boolean;
  createdAt: string;
  /** `null` indica tópico global (do sistema) — bloqueamos edição nesse caso. */
  municipioId?: number | string | null;
}

interface Categoria {
  id: string;
  label: string;
  icon: LucideIcon;
  color: string;
}

const STATUS_OPTIONS = [
  "Aguardando Aprovação",
  "Aprovado",
  "Reprovado",
] as const;
type EditStatus = (typeof STATUS_OPTIONS)[number];

const TIPO_OPTIONS = ["Problema", "Evento", "Sugestão"] as const;
type EditTipo = (typeof TIPO_OPTIONS)[number];

const normalizeText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "");

const normalizeMetodoValidacao = (value: unknown): "agente" | "ia" => {
  if (typeof value !== "string") return "agente";
  const normalized = normalizeText(value);
  return normalized.includes("ia") || normalized.includes("inteligencia")
    ? "ia"
    : "agente";
};

const normalizeStatus = (value: unknown): EditStatus => {
  if (typeof value !== "string") return "Aguardando Aprovação";
  const normalized = normalizeText(value);
  if (normalized === "aprovado") return "Aprovado";
  if (normalized === "reprovado") return "Reprovado";
  return "Aguardando Aprovação";
};

const normalizeTipo = (value: unknown): EditTipo => {
  if (typeof value !== "string") return "Problema";
  const normalized = normalizeText(value);
  if (normalized === "evento") return "Evento";
  if (normalized === "sugestao") return "Sugestão";
  return "Problema";
};

const getTopicoPayload = (
  value: Record<string, unknown>,
): Record<string, unknown> => {
  const nestedKeys = ["data", "topico", "item"] as const;

  if ("id" in value) return value;

  for (const key of nestedKeys) {
    const nestedValue = value[key];
    if (nestedValue && typeof nestedValue === "object" && "id" in nestedValue) {
      return nestedValue as Record<string, unknown>;
    }
  }

  return value;
};

const CATEGORIAS_INICIAIS: Categoria[] = [
  {
    id: "vias_transito",
    label: "Vias e Trânsito",
    icon: TrafficCone,
    color:
      "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  },
  {
    id: "saude",
    label: "Saúde",
    icon: HeartPulse,
    color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  },
  {
    id: "transporte_publico",
    label: "Transporte Público",
    icon: Bus,
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  },
  {
    id: "seguranca",
    label: "Segurança",
    icon: ShieldAlert,
    color:
      "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  },
  {
    id: "meio_ambiente",
    label: "Meio Ambiente",
    icon: Leaf,
    color:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  },
  {
    id: "educacao",
    label: "Educação",
    icon: GraduationCap,
    color:
      "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
  },
  {
    id: "saneamento",
    label: "Saneamento",
    icon: Droplets,
    color: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400",
  },
  {
    id: "administracao",
    label: "Administração Pública",
    icon: Landmark,
    color:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  },
  {
    id: "iluminacao",
    label: "Iluminação Pública",
    icon: Lightbulb,
    color:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  },
  {
    id: "outros",
    label: "Outros",
    icon: HelpCircle,
    color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  },
];

const CORES_DISPONIVEIS = [
  "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400",
  "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400",
  "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400",
  "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400",
  "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400",
  "bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-400",
  "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-400",
];

const TOPICOS_MOCK: TopicoCard[] = [
  {
    id: "1",
    nome: "Buracos nas vias",
    categoria: "vias_transito",
    descricao:
      "Relatos sobre buracos, crateras e irregularidades no asfalto das vias públicas.",
    contexto: "Inclui vias urbanas e rurais do município.",
    metodoValidacao: "ia",
    ativo: true,
    arquivado: false,
    createdAt: "2025-01-15",
  },
  {
    id: "2",
    nome: "Semáforos com defeito",
    categoria: "vias_transito",
    descricao:
      "Semáforos apagados, com mau funcionamento ou temporização inadequada.",
    contexto: "Semáforos de veículos e pedestres.",
    metodoValidacao: "agente",
    ativo: true,
    arquivado: false,
    createdAt: "2025-01-20",
  },
  {
    id: "3",
    nome: "Falta de medicamentos",
    categoria: "saude",
    descricao:
      "Reclamações sobre falta de medicamentos em postos de saúde e farmácias municipais.",
    contexto: "Medicamentos da lista básica do SUS.",
    metodoValidacao: "agente",
    ativo: true,
    arquivado: false,
    createdAt: "2025-02-01",
  },
  {
    id: "4",
    nome: "Atraso no transporte",
    categoria: "transporte_publico",
    descricao: "Ônibus atrasados ou que não cumprem os horários estabelecidos.",
    contexto: "Linhas urbanas e metropolitanas.",
    metodoValidacao: "ia",
    ativo: true,
    arquivado: false,
    createdAt: "2025-02-10",
  },
  {
    id: "5",
    nome: "Iluminação precária",
    categoria: "iluminacao",
    descricao: "Postes apagados, lâmpadas queimadas ou áreas sem iluminação.",
    contexto: "Vias públicas, praças e áreas comuns.",
    metodoValidacao: "ia",
    ativo: true,
    arquivado: false,
    createdAt: "2025-02-15",
  },
  {
    id: "6",
    nome: "Poluição de rios",
    categoria: "meio_ambiente",
    descricao:
      "Denúncias sobre despejo irregular de esgoto ou lixo em rios e córregos.",
    contexto: "Recursos hídricos dentro do perímetro municipal.",
    metodoValidacao: "agente",
    ativo: true,
    arquivado: false,
    createdAt: "2025-03-01",
  },
  {
    id: "7",
    nome: "Falta de vagas em creches",
    categoria: "educacao",
    descricao: "Demanda por vagas em creches municipais.",
    contexto: "Creches e centros de educação infantil.",
    metodoValidacao: "agente",
    ativo: false,
    arquivado: true,
    createdAt: "2025-03-05",
  },
  {
    id: "8",
    nome: "Esgoto a céu aberto",
    categoria: "saneamento",
    descricao: "Reclamações sobre esgoto exposto em vias públicas.",
    contexto: "Saneamento básico em áreas urbanas e periféricas.",
    metodoValidacao: "ia",
    ativo: true,
    arquivado: false,
    createdAt: "2025-03-10",
  },
];

export default function TopicosPage() {
  const [categoriasState, setCategoriasState] =
    useState<Categoria[]>(CATEGORIAS_INICIAIS);
  const [topicos, setTopicos] = useState<TopicoCard[]>(TOPICOS_MOCK);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTopico, setEditingTopico] = useState<TopicoFull | null>(null);
  const [viewingTopico, setViewingTopico] = useState<TopicoFull | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [isLoadingView, setIsLoadingView] = useState(false);
  const [categoriaDialogOpen, setCategoriaDialogOpen] = useState(false);
  const [novaCategoriaName, setNovaCategoriaName] = useState("");
  const [step, setStep] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { toast } = useToast();

  const {
    topicos: topicosApi,
    isLoading,
    error,
    fetchTopicos,
    addTopico,
  } = useTopicosStore();
  const { isReadOnly } = useAgentePermissions();

  const ouvidoria = useCurrentOuvidoriaStore((s) => s.ouvidoria);
  const fetchCurrentOuvidoria = useCurrentOuvidoriaStore(
    (s) => s.fetchCurrentOuvidoria,
  );
  const ouvidoriaHasFetched = useCurrentOuvidoriaStore((s) => s.hasFetched);

  // Garante que a ouvidoria atual esteja carregada (necessária para extrair as etiquetas)
  useEffect(() => {
    if (!ouvidoriaHasFetched) {
      fetchCurrentOuvidoria();
    }
  }, [ouvidoriaHasFetched, fetchCurrentOuvidoria]);

  // Etiquetas da ouvidoria atual — normaliza formatos possíveis vindos da API
  // (array de strings, array de objetos { nome | etiqueta | descricao }, ou
  // strings com múltiplos valores separados por vírgula) e deduplica.
  const ouvidoriaEtiquetas = useMemo<string[]>(() => {
    const raw = ouvidoria?.etiquetas as unknown;
    const collected: string[] = [];

    const pushFrom = (value: unknown) => {
      if (value == null) return;
      if (typeof value === "string") {
        value
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
          .forEach((v) => collected.push(v));
        return;
      }
      if (typeof value === "object") {
        const obj = value as Record<string, unknown>;
        const candidate =
          obj.nome ?? obj.etiqueta ?? obj.descricao ?? obj.label;
        if (typeof candidate === "string") pushFrom(candidate);
      }
    };

    if (Array.isArray(raw)) raw.forEach(pushFrom);
    else pushFrom(raw);

    return Array.from(new Set(collected));
  }, [ouvidoria?.etiquetas]);

  const etiquetasKey = ouvidoriaEtiquetas.join("|");

  // Carrega tópicos filtrados pelas etiquetas da ouvidoria atual.
  // Enquanto a ouvidoria não foi carregada, evitamos disparar o fetch genérico
  // (que traria todos os tópicos) para não causar flash de dados não-pertinentes.
  const reloadTopicos = useCallback(() => {
    if (!ouvidoriaHasFetched) return;
    fetchTopicos({
      etiquetas: ouvidoriaEtiquetas,
      municipioId: AppConfig.municipioLong,
    });
  }, [fetchTopicos, ouvidoriaHasFetched, etiquetasKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reloadTopicos();
  }, [reloadTopicos]);

  // Sempre que a página ganhar foco ou voltar à aba, recarrega a lista
  useEffect(() => {
    const handleFocus = () => reloadTopicos();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") reloadTopicos();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [reloadTopicos]);

  useEffect(() => {
    const mapped: TopicoCard[] = topicosApi.map((t) => ({
      id: t.id,
      nome: t.descricao,
      categoria: t.etiquetas?.[0] || "sem_categoria",
      descricao: t.descricao,
      contexto: t.contexto || "",
      metodoValidacao: t.metodoValidacao === "IA" ? "ia" : "agente",
      ativo: t.ativo,
      arquivado: Boolean(t.arquivado),
      createdAt: "",
      municipioId: t.municipioId ?? null,
    }));
    setTopicos(mapped);
  }, [topicosApi]);

  // Form state (create)
  const [selectedCategoria, setSelectedCategoria] = useState<string>("");
  const [descricao, setDescricao] = useState("");
  const [contexto, setContexto] = useState("");
  const [metodoValidacao, setMetodoValidacao] = useState<"agente" | "ia">(
    "agente",
  );

  // Form state (edit)
  const [editDescricao, setEditDescricao] = useState("");
  const [editContexto, setEditContexto] = useState("");
  const [editMetodoValidacao, setEditMetodoValidacao] = useState<
    "agente" | "ia"
  >("agente");
  const [editCategoria, setEditCategoria] = useState("");
  const [editEtiquetas, setEditEtiquetas] = useState<string[]>([]);
  const [editStatus, setEditStatus] = useState<EditStatus>(
    "Aguardando Aprovação",
  );
  const [editTipo, setEditTipo] = useState<EditTipo>("Problema");
  const [editAtivo, setEditAtivo] = useState(true);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const totalSteps = 3;

  const dynamicCategorias: Categoria[] = useMemo(() => {
    const unique = new Map<string, Categoria>();
    topicos.forEach((t, idx) => {
      const id = t.categoria || "sem_categoria";
      if (!unique.has(id)) {
        const color = CORES_DISPONIVEIS[idx % CORES_DISPONIVEIS.length];
        unique.set(id, { id, label: id.replace(/_/g, " "), icon: Tag, color });
      }
    });
    return Array.from(unique.values());
  }, [topicos]);

  const categoriasVisiveis = dynamicCategorias.length
    ? dynamicCategorias
    : categoriasState;

  const filteredTopicos = topicos.filter((t) => {
    const matchSearch = `${t.descricao} ${t.contexto}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchCategoria =
      filterCategoria === "all" || t.categoria === filterCategoria;
    return matchSearch && matchCategoria;
  });

  const totalPages = Math.max(1, Math.ceil(filteredTopicos.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedTopicos = filteredTopicos.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const showingFrom =
    filteredTopicos.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const showingTo = Math.min(currentPage * pageSize, filteredTopicos.length);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterCategoria, topicos]);

  const getCategoriaInfo = (id: string) =>
    categoriasVisiveis.find((c) => c.id === id);

  const handleAddCategoria = () => {
    if (!novaCategoriaName.trim()) {
      toast({ title: "Digite o nome da categoria", variant: "destructive" });
      return;
    }
    const id = novaCategoriaName
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
    if (categoriasState.some((c) => c.id === id)) {
      toast({ title: "Categoria já existe", variant: "destructive" });
      return;
    }
    const colorIndex =
      (categoriasState.length - CATEGORIAS_INICIAIS.length) %
      CORES_DISPONIVEIS.length;
    const newCat: Categoria = {
      id,
      label: novaCategoriaName.trim(),
      icon: Tag,
      color: CORES_DISPONIVEIS[colorIndex],
    };
    setCategoriasState((prev) => [...prev, newCat]);
    setNovaCategoriaName("");
    setCategoriaDialogOpen(false);
    toast({ title: "Categoria criada com sucesso!" });
  };

  const handleArchiveRestore = async (topico: TopicoCard) => {
    setActionLoading(topico.id);
    try {
      const query = new Query({ params: { topicoId: topico.id } });
      const result = topico.arquivado
        ? await topicoRepo.restaurarTopico(query)
        : await topicoRepo.arquivarTopico(query);

      if (!result.isSuccess || result.failure) {
        const failure = result.failure;
        const msg =
          (failure as { messages?: string[]; message?: string } | undefined)
            ?.messages?.[0] ||
          (failure as { message?: string } | undefined)?.message ||
          "Erro ao atualizar tópico";
        throw new Error(msg);
      }

      reloadTopicos();
      setTopicos((prev) =>
        prev.map((t) =>
          t.id === topico.id ? { ...t, arquivado: !topico.arquivado } : t,
        ),
      );
      toast({
        title: topico.arquivado ? "Tópico restaurado" : "Tópico arquivado",
        variant: "default",
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao atualizar tópico";
      toast({ title: message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (topico: TopicoCard) => {
    setActionLoading(topico.id);
    try {
      const query = new Query({ params: { topicoId: topico.id } });
      const result = await topicoRepo.removerTopico(query);

      if (!result.isSuccess || result.failure) {
        const failure = result.failure;
        const msg =
          (failure as { messages?: string[]; message?: string } | undefined)
            ?.messages?.[0] ||
          (failure as { message?: string } | undefined)?.message ||
          "Erro ao remover tópico";
        throw new Error(msg);
      }

      reloadTopicos();
      setTopicos((prev) => prev.filter((t) => t.id !== topico.id));
      toast({ title: "Tópico removido com sucesso" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao remover tópico";
      toast({ title: message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedCategoria("");
    setDescricao("");
    setContexto("");
    setMetodoValidacao("agente");
  };

  const handleOpenDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleNext = async () => {
    if (step === 1 && !selectedCategoria) {
      toast({ title: "Selecione uma categoria", variant: "destructive" });
      return;
    }
    if (step === 3 && !descricao.trim()) {
      toast({ title: "Preencha a descrição", variant: "destructive" });
      return;
    }
    if (step === 3 && metodoValidacao === "ia" && !contexto.trim()) {
      toast({
        title: "Preencha o contexto para validação por IA",
        variant: "destructive",
      });
      return;
    }
    if (step === 3) {
      await handleSubmit();
      return;
    }
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    if (metodoValidacao === "ia" && !contexto.trim()) {
      toast({
        title: "Preencha o contexto para validação por IA",
        variant: "destructive",
      });
      return;
    }
    const payload: TopicoState = {
      descricao: descricao.trim(),
      contexto: metodoValidacao === "ia" ? contexto.trim() : undefined,
      metodoValidacao: metodoValidacao === "ia" ? "IA" : "Agente",
      etiquetas: selectedCategoria ? [selectedCategoria] : undefined,
      tipo: "Problema",
      status: "Ativo",
      municipioId: AppConfig.municipioLong,
    };

    try {
      await addTopico(payload);
      reloadTopicos();
      setDialogOpen(false);
      resetForm();
      toast({
        title: "✅ Tópico criado com sucesso!",
        className: "bg-emerald-50 text-emerald-900 border border-emerald-200",
      });
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Erro ao criar tópico";
      toast({ title: message, variant: "destructive" });
    }
  };

  const handleViewTopico = async (topico: TopicoCard) => {
    setViewDialogOpen(true);
    setIsLoadingView(true);
    try {
      const query = new Query({ params: { topicoId: topico.id } });
      const result = await topicoRepo.obterTopico(query);

      if (!result.isSuccess) {
        const failure = result.failure;
        const msg =
          (failure as { messages?: string[]; message?: string } | undefined)
            ?.messages?.[0] ||
          (failure as { message?: string } | undefined)?.message ||
          "Erro ao carregar tópico";
        throw new Error(msg);
      }

      const responsePayload = getTopicoPayload(
        result.success as Record<string, unknown>,
      );

      // Extract first element if response is an array
      const topicoData =
        Array.isArray(responsePayload) && responsePayload.length > 0
          ? (responsePayload[0] as Record<string, unknown>)
          : (responsePayload as Record<string, unknown>);

      const full = TopicoModel.fromMap(topicoData);
      setViewingTopico(full);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao carregar tópico";
      toast({ title: message, variant: "destructive" });
      setViewDialogOpen(false);
    } finally {
      setIsLoadingView(false);
    }
  };

  const handleOpenEdit = async (topico: TopicoCard) => {
    // Bloqueia edição de tópicos globais (municipioId == null) — pertencem ao
    // sistema e não podem ser alterados pela ouvidoria/município.
    if (topico.municipioId == null) {
      toast({
        title: "Edição não permitida",
        description:
          "Este tópico é global (do sistema) e não pode ser editado pela ouvidoria.",
        variant: "destructive",
      });
      return;
    }

    setEditDialogOpen(true);
    setIsLoadingEdit(true);
    try {
      const query = new Query({ params: { topicoId: topico.id } });
      const result = await topicoRepo.obterTopico(query);

      if (!result.isSuccess) {
        const failure = result.failure;
        const msg =
          (failure as { messages?: string[]; message?: string } | undefined)
            ?.messages?.[0] ||
          (failure as { message?: string } | undefined)?.message ||
          "Erro ao carregar tópico";
        throw new Error(msg);
      }

      const responsePayload = getTopicoPayload(
        result.success as Record<string, unknown>,
      );

      // Extract first element if response is an array
      const topicoData =
        Array.isArray(responsePayload) && responsePayload.length > 0
          ? (responsePayload[0] as Record<string, unknown>)
          : (responsePayload as Record<string, unknown>);

      const full = TopicoModel.fromMap(topicoData);
      const etiquetas = Array.isArray(full.etiquetas)
        ? full.etiquetas.filter((tag): tag is string => Boolean(tag?.trim()))
        : [];
      const categoriaPrincipal =
        etiquetas[0] || topico.categoria || "sem_categoria";

      setEditingTopico(full);
      setEditDescricao(full.descricao || topico.descricao || "");
      setEditContexto(full.contexto || "");
      setEditMetodoValidacao(normalizeMetodoValidacao(full.metodoValidacao));
      setEditCategoria(categoriaPrincipal);
      setEditEtiquetas(
        etiquetas.length
          ? etiquetas
          : categoriaPrincipal
            ? [categoriaPrincipal]
            : [],
      );
      setEditStatus(normalizeStatus(full.status));
      setEditTipo(normalizeTipo(full.tipo));
      setEditAtivo(typeof full.ativo === "boolean" ? full.ativo : topico.ativo);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao carregar tópico";
      toast({ title: message, variant: "destructive" });
      setEditDialogOpen(false);
    } finally {
      setIsLoadingEdit(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingTopico) return;
    if (!editDescricao.trim()) {
      toast({ title: "Preencha a descrição", variant: "destructive" });
      return;
    }
    if (editMetodoValidacao === "ia" && !editContexto.trim()) {
      toast({
        title: "Preencha o contexto para validação por IA",
        variant: "destructive",
      });
      return;
    }

    const novoContexto =
      editMetodoValidacao === "ia" ? editContexto.trim() : "";
    const novoMetodo = editMetodoValidacao === "ia" ? "IA" : "Agente";
    const etiquetasPayload = editEtiquetas.length
      ? editEtiquetas
      : editCategoria
        ? [editCategoria]
        : [];

    // Compare with original values to create JSON Patch operations
    const operations: Array<{ op: string; path: string; value: unknown }> = [];

    if (editDescricao.trim() !== editingTopico.descricao) {
      operations.push({
        op: "replace",
        path: "/descricao",
        value: editDescricao.trim(),
      });
    }

    if (novoContexto !== (editingTopico.contexto || "")) {
      operations.push({
        op: "replace",
        path: "/contexto",
        value: novoContexto || undefined,
      });
    }

    if (novoMetodo !== editingTopico.metodoValidacao) {
      operations.push({
        op: "replace",
        path: "/metodoValidacao",
        value: novoMetodo,
      });
    }

    const originalEtiquetas = Array.isArray(editingTopico.etiquetas)
      ? editingTopico.etiquetas.filter((tag): tag is string =>
          Boolean(tag?.trim()),
        )
      : [];
    if (
      JSON.stringify(etiquetasPayload) !== JSON.stringify(originalEtiquetas)
    ) {
      operations.push({
        op: "replace",
        path: "/etiquetas",
        value: etiquetasPayload,
      });
    }

    if (editTipo !== editingTopico.tipo) {
      operations.push({
        op: "replace",
        path: "/tipo",
        value: editTipo,
      });
    }

    if (editStatus !== normalizeStatus(editingTopico.status)) {
      operations.push({
        op: "replace",
        path: "/status",
        value: editStatus,
      });
    }

    if (editAtivo !== editingTopico.ativo) {
      operations.push({
        op: "replace",
        path: "/ativo",
        value: editAtivo,
      });
    }

    if (operations.length === 0) {
      toast({ title: "Nenhuma alteração detectada", variant: "default" });
      setEditDialogOpen(false);
      setEditingTopico(null);
      return;
    }

    setIsSavingEdit(true);
    try {
      const query = new Query({ params: { topicoId: editingTopico.id } });
      const result = await topicoRepo.atualizarTopicoPatch(query, operations);

      if (!result.isSuccess) {
        const failure = result.failure;
        const msg =
          (failure as { messages?: string[]; message?: string } | undefined)
            ?.messages?.[0] ||
          (failure as { message?: string } | undefined)?.message ||
          "Erro ao atualizar tópico";
        throw new Error(msg);
      }

      reloadTopicos();
      setEditDialogOpen(false);
      setEditingTopico(null);
      toast({ title: "Tópico atualizado com sucesso!" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao atualizar tópico";
      toast({ title: message, variant: "destructive" });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const totalByCategoria = (catId: string) =>
    topicos.filter((t) => t.categoria === catId).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">
            Tópicos
          </h2>
          <p className="text-muted-foreground text-sm">
            Gerencie os tópicos tratados pela ouvidoria
          </p>
        </div>
        {!isReadOnly && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCategoriaDialogOpen(true)}
              className="gap-2"
            >
              <FolderPlus className="h-4 w-4" />
              Nova Categoria
            </Button>
            <Button onClick={handleOpenDialog} className="gap-2">
              <Plus className="h-4 w-4" />
              Novo Tópico
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-2">
          {error}
        </div>
      )}

      {isLoading && topicos.length === 0 && (
        <div className="text-sm text-muted-foreground">
          Carregando tópicos...
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-caption text-xs">Total de Tópicos</p>
            <p className="text-2xl font-heading font-bold text-foreground">
              {topicos.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-caption text-xs">Ativos</p>
            <p className="text-2xl font-heading font-bold text-primary">
              {topicos.filter((t) => t.ativo).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-caption text-xs">Categorias</p>
            <p className="text-2xl font-heading font-bold text-foreground">
              {new Set(topicos.map((t) => t.categoria)).size}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-caption text-xs">Validação por IA</p>
            <p className="text-2xl font-heading font-bold text-secondary">
              {topicos.filter((t) => t.metodoValidacao === "ia").length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Categories Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Categorias
        </h3>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterCategoria("all")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
              filterCategoria === "all"
                ? "border-primary bg-primary/10 text-primary ring-1 ring-primary/30"
                : "border-border bg-card hover:border-primary/30 hover:bg-accent/50 text-foreground"
            }`}
          >
            <Tag className="h-4 w-4" />
            Todos
            <span
              className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                filterCategoria === "all"
                  ? "bg-primary/20 text-primary"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {topicos.length}
            </span>
          </button>
          {categoriasVisiveis.map((cat) => {
            const count = totalByCategoria(cat.id);
            const isActive = filterCategoria === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setFilterCategoria(isActive ? "all" : cat.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  isActive
                    ? "border-primary bg-primary/10 text-primary ring-1 ring-primary/30"
                    : "border-border bg-card hover:border-primary/30 hover:bg-accent/50 text-foreground"
                }`}
              >
                <div
                  className={`h-6 w-6 rounded-md flex items-center justify-center shrink-0 ${cat.color}`}
                >
                  <cat.icon className="h-3.5 w-3.5" />
                </div>
                {cat.label}
                {count > 0 && (
                  <span
                    className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                      isActive
                        ? "bg-primary/20 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar tópico..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Topics List */}
      <div className="grid gap-3">
        <AnimatePresence mode="popLayout">
          {paginatedTopicos.map((topico) => {
            const cat = getCategoriaInfo(topico.categoria);
            const CatIcon = cat?.icon || Tag;
            return (
              <motion.div
                key={topico.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div
                      className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${cat?.color || ""}`}
                    >
                      <CatIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-foreground truncate">
                          {topico.nome}
                        </h3>
                        {!topico.ativo && (
                          <Badge variant="secondary" className="text-xs">
                            Inativo
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {topico.descricao}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="gap-1 text-xs">
                        <Tag className="h-3 w-3" />
                        {cat?.label}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={`gap-1 text-xs ${
                          topico.metodoValidacao === "ia"
                            ? "border-secondary/50 text-secondary"
                            : "border-primary/50 text-primary"
                        }`}
                      >
                        {topico.metodoValidacao === "ia" ? (
                          <Bot className="h-3 w-3" />
                        ) : (
                          <UserCheck className="h-3 w-3" />
                        )}
                        {topico.metodoValidacao === "ia" ? "IA" : "Agente"}
                      </Badge>
                      {!isReadOnly && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              disabled={actionLoading === topico.id}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleViewTopico(topico)}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Visualizar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleOpenEdit(topico)}
                              disabled={topico.municipioId == null}
                              title={
                                topico.municipioId == null
                                  ? "Tópico global do sistema — não pode ser editado"
                                  : undefined
                              }
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleArchiveRestore(topico)}
                            >
                              {topico.arquivado ? (
                                <RotateCcw className="h-4 w-4 mr-2" />
                              ) : (
                                <Archive className="h-4 w-4 mr-2" />
                              )}
                              {topico.arquivado ? "Restaurar" : "Arquivar"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleRemove(topico)}
                              disabled={actionLoading === topico.id}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {filteredTopicos.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Tag className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>Nenhum tópico encontrado</p>
          </div>
        )}
        {filteredTopicos.length > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
            <p className="text-sm text-muted-foreground">
              Mostrando {showingFrom} a {showingTo} de {filteredTopicos.length}{" "}
              tópicos
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>
              {totalPages > 0 && (
                <>
                  {currentPage > 2 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-w-[40px]"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </Button>
                      {currentPage > 3 && (
                        <span className="text-muted-foreground">...</span>
                      )}
                    </>
                  )}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const offset = Math.max(
                      0,
                      Math.min(currentPage - 3, totalPages - 5),
                    );
                    const pageNum = offset + i + 1;
                    if (pageNum < 1 || pageNum > totalPages) return null;
                    return (
                      <Button
                        key={pageNum}
                        variant={
                          currentPage === pageNum ? "default" : "outline"
                        }
                        size="sm"
                        className="min-w-[40px]"
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  {currentPage < totalPages - 1 && totalPages > 5 && (
                    <>
                      {currentPage < totalPages - 2 && (
                        <span className="text-muted-foreground">...</span>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-w-[40px]"
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Topic Dialog - Multi Step */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Novo Tópico</DialogTitle>
            <DialogDescription>
              Etapa {step} de 3 —{" "}
              {step === 1
                ? "Categoria"
                : step === 2
                  ? "Método de Validação"
                  : "Detalhes"}
            </DialogDescription>
          </DialogHeader>

          {/* Progress */}
          <div className="flex gap-1.5">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  s <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>

          {/* Step 1: Category */}
          {step === 1 && (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
              <p className="text-sm text-muted-foreground">
                Selecione a categoria do tópico:
              </p>
              <div className="grid grid-cols-2 gap-2">
                {categoriasVisiveis.map((cat) => {
                  const isSelected = selectedCategoria === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategoria(cat.id)}
                      className={`flex items-center gap-2.5 p-3 rounded-lg border text-left text-sm transition-all ${
                        isSelected
                          ? "border-primary bg-primary/10 ring-1 ring-primary"
                          : "border-border hover:border-primary/40 hover:bg-accent/50"
                      }`}
                    >
                      <div
                        className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${cat.color}`}
                      >
                        <cat.icon className="h-4 w-4" />
                      </div>
                      <span className="font-medium text-foreground">
                        {cat.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Validation Method */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Escolha como as solicitações deste tópico serão validadas:
              </p>
              <RadioGroup
                value={metodoValidacao}
                onValueChange={(v) => setMetodoValidacao(v as "agente" | "ia")}
                className="grid gap-3"
              >
                <label
                  htmlFor="val-agente"
                  className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                    metodoValidacao === "agente"
                      ? "border-primary bg-primary/10 ring-1 ring-primary"
                      : "border-border hover:border-primary/40"
                  }`}
                >
                  <RadioGroupItem
                    value="agente"
                    id="val-agente"
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <UserCheck className="h-4 w-4 text-primary" />
                      <span className="font-medium text-foreground">
                        Agente
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Um agente humano revisará e validará cada solicitação
                      manualmente.
                    </p>
                  </div>
                </label>

                <label
                  htmlFor="val-ia"
                  className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                    metodoValidacao === "ia"
                      ? "border-secondary bg-secondary/10 ring-1 ring-secondary"
                      : "border-border hover:border-secondary/40"
                  }`}
                >
                  <RadioGroupItem value="ia" id="val-ia" className="mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Bot className="h-4 w-4 text-secondary" />
                      <span className="font-medium text-foreground">
                        Inteligência Artificial
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      A IA analisará automaticamente a solicitação com base no
                      contexto configurado.
                    </p>
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}

          {/* Step 3: Description (+ Context if IA) */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição do Tópico</Label>
                <Textarea
                  id="descricao"
                  placeholder="Descreva o que este tópico abrange. Ex: Buracos nas vias públicas, crateras e irregularidades no asfalto."
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={3}
                />
              </div>

              {metodoValidacao === "ia" && (
                <>
                  <div className="rounded-lg border border-secondary/30 bg-secondary/5 p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Bot className="h-5 w-5 text-secondary" />
                      <span className="font-medium text-foreground">
                        Contexto para a IA
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      O contexto serve para dar mais informações à inteligência
                      artificial sobre este tópico. Quanto mais detalhado, mais
                      precisa será a validação automática das solicitações.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contexto">Contexto</Label>
                    <Textarea
                      id="contexto"
                      placeholder="Descreva o contexto deste tópico para a IA. Ex: Inclui vias urbanas e rurais do município, considerar apenas buracos com mais de 30cm de diâmetro..."
                      value={contexto}
                      onChange={(e) => setContexto(e.target.value)}
                      rows={5}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => setStep((s) => s - 1)}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </Button>
            )}
            {step < 3 ? (
              <Button
                onClick={() => void handleNext()}
                disabled={isLoading}
                className="gap-1"
              >
                Próximo
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={() => void handleNext()}
                disabled={isLoading}
                className="gap-1"
              >
                <Plus className="h-4 w-4" />
                {isLoading ? "Enviando..." : "Criar Tópico"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={categoriaDialogOpen} onOpenChange={setCategoriaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Nova Categoria</DialogTitle>
            <DialogDescription>
              Adicione uma nova categoria para organizar os tópicos da
              ouvidoria.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome-categoria">Nome da Categoria</Label>
              <Input
                id="nome-categoria"
                placeholder="Ex: Assistência Social"
                value={novaCategoriaName}
                onChange={(e) => setNovaCategoriaName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddCategoria()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCategoriaDialogOpen(false);
                setNovaCategoriaName("");
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleAddCategoria} className="gap-1">
              <Plus className="h-4 w-4" />
              Criar Categoria
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Topic Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Tópico</DialogTitle>
            <DialogDescription>
              Altere os campos desejados e salve as mudanças.
            </DialogDescription>
          </DialogHeader>

          {isLoadingEdit && (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-primary/40 border-t-primary animate-spin" />
              Carregando dados atuais do tópico...
            </div>
          )}

          <div
            className={`space-y-5 ${isLoadingEdit ? "pointer-events-none opacity-60" : ""}`}
          >
            {/* Categoria */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Categoria</Label>
              <div className="grid grid-cols-2 gap-3 max-h-[220px] overflow-y-auto pr-1">
                {categoriasVisiveis.map((cat) => {
                  const isSelected = editCategoria === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setEditCategoria(cat.id);
                        setEditEtiquetas([cat.id]);
                      }}
                      className={`flex items-center gap-3 p-3 rounded-xl border text-left text-sm transition-all ${
                        isSelected
                          ? "border-primary bg-primary/10 ring-1 ring-primary/60 shadow-sm"
                          : "border-border hover:border-primary/40 hover:bg-accent/60"
                      }`}
                    >
                      <div
                        className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${cat.color}`}
                      >
                        <cat.icon className="h-3.5 w-3.5" />
                      </div>
                      <span className="font-medium text-foreground text-xs">
                        {cat.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tipo */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Tipo</Label>
              <RadioGroup
                value={editTipo}
                onValueChange={(v) => setEditTipo(v as EditTipo)}
                className="grid grid-cols-3 gap-3"
              >
                {TIPO_OPTIONS.map((tipo) => (
                  <label
                    key={tipo}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border cursor-pointer text-sm transition-all ${
                      editTipo === tipo
                        ? "border-primary bg-primary/10 ring-1 ring-primary/60 shadow-sm"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <RadioGroupItem value={tipo} id={`edit-tipo-${tipo}`} />
                    <span className="font-medium text-foreground text-xs">
                      {tipo}
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Status */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Status</Label>
              <RadioGroup
                value={editStatus}
                onValueChange={(v) => setEditStatus(v as EditStatus)}
                className="grid grid-cols-3 gap-3"
              >
                {STATUS_OPTIONS.map((status) => (
                  <label
                    key={status}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border cursor-pointer text-xs transition-all ${
                      editStatus === status
                        ? "border-primary bg-primary/10 ring-1 ring-primary/60 shadow-sm"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <RadioGroupItem
                      value={status}
                      id={`edit-status-${status}`}
                    />
                    <span className="font-medium text-foreground text-[11px] leading-tight text-center">
                      {status}
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Método de Validação */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">
                Método de Validação
              </Label>
              <RadioGroup
                value={editMetodoValidacao}
                onValueChange={(v) =>
                  setEditMetodoValidacao(v as "agente" | "ia")
                }
                className="grid grid-cols-2 gap-4"
              >
                <label
                  htmlFor="edit-val-agente"
                  className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    editMetodoValidacao === "agente"
                      ? "border-primary bg-primary/10 ring-1 ring-primary/60 shadow-sm"
                      : "border-border hover:border-primary/40"
                  }`}
                >
                  <RadioGroupItem value="agente" id="edit-val-agente" />
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-primary" />
                    <span className="font-medium text-foreground text-sm">
                      Agente
                    </span>
                  </div>
                </label>
                <label
                  htmlFor="edit-val-ia"
                  className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    editMetodoValidacao === "ia"
                      ? "border-secondary bg-secondary/10 ring-1 ring-secondary/60 shadow-sm"
                      : "border-border hover:border-secondary/40"
                  }`}
                >
                  <RadioGroupItem value="ia" id="edit-val-ia" />
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-secondary" />
                    <span className="font-medium text-foreground text-sm">
                      IA
                    </span>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Ativo */}
            <div className="flex items-center justify-between rounded-lg border border-border/70 px-3 py-2.5">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Tópico ativo
                </p>
                <p className="text-xs text-muted-foreground">
                  Controla se o tópico está disponível.
                </p>
              </div>
              <Switch checked={editAtivo} onCheckedChange={setEditAtivo} />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="edit-descricao">Descrição</Label>
              <Textarea
                id="edit-descricao"
                value={editDescricao}
                onChange={(e) => setEditDescricao(e.target.value)}
                rows={3}
              />
            </div>

            {/* Contexto (only if IA) */}
            {editMetodoValidacao === "ia" && (
              <div className="space-y-2">
                <div className="rounded-lg border border-secondary/30 bg-secondary/5 p-3 flex items-center gap-2">
                  <Bot className="h-4 w-4 text-secondary" />
                  <span className="text-sm text-muted-foreground">
                    Contexto utilizado pela IA para validar solicitações.
                  </span>
                </div>
                <Label htmlFor="edit-contexto">Contexto</Label>
                <Textarea
                  id="edit-contexto"
                  value={editContexto}
                  onChange={(e) => setEditContexto(e.target.value)}
                  rows={4}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveEdit}
              className="gap-1"
              disabled={isSavingEdit || isLoadingEdit || !editingTopico}
            >
              <Save className="h-4 w-4" />
              {isSavingEdit ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Topic Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Detalhes do Tópico
            </DialogTitle>
            <DialogDescription>
              Visualização completa do tópico
            </DialogDescription>
          </DialogHeader>

          {isLoadingView ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : viewingTopico ? (
            <div className="space-y-4 py-2">
              {/* Descrição */}
              <div className="space-y-2">
                <Label>Descrição</Label>
                <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                  {viewingTopico.descricao || "-"}
                </div>
              </div>

              {/* Contexto */}
              <div className="space-y-2">
                <Label>Contexto</Label>
                <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                  {viewingTopico.contexto || "-"}
                </div>
              </div>

              {/* Etiquetas */}
              <div className="space-y-2">
                <Label>Etiquetas</Label>
                <div className="flex flex-wrap gap-2">
                  {viewingTopico.etiquetas?.length ? (
                    viewingTopico.etiquetas.map((tag) => (
                      <Badge key={tag} variant="outline" className="gap-1">
                        <Tag className="h-3 w-3" />
                        {tag}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </div>
              </div>

              {/* Grid de informações */}
              <div className="grid grid-cols-2 gap-4">
                {/* Status */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                    {viewingTopico.status || "-"}
                  </div>
                </div>

                {/* Tipo */}
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                    {viewingTopico.tipo || "-"}
                  </div>
                </div>

                {/* Método de Validação */}
                <div className="space-y-2">
                  <Label>Método de Validação</Label>
                  <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                    {viewingTopico.metodoValidacao === "IA" ? (
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-secondary" />
                        <span>IA</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-primary" />
                        <span>Agente</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Ativo */}
                <div className="space-y-2">
                  <Label>Ativo</Label>
                  <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                    {viewingTopico.ativo ? (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Sim</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>Não</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Arquivado */}
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
                <span className="text-sm font-medium">Tópico arquivado</span>
                <span className="text-sm">
                  {viewingTopico.arquivado ? (
                    <Badge variant="secondary">Arquivado</Badge>
                  ) : (
                    <span className="text-muted-foreground">Não</span>
                  )}
                </span>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button onClick={() => setViewDialogOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
